import { useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { useInterval } from "@/lib/hooks/useInterval";
import { Badge, Button, Card, CardSection, EmptyState, Select, useToast } from "@/components/ui";
import { formatRelative } from "@/lib/utils/format";
import type { PrintJobStatus } from "@/lib/types/api";
import { IconPrinter, IconRefresh } from "../components/icons";

const STATUS_TONES: Record<PrintJobStatus, "neutral" | "aqua" | "accent" | "warn" | "danger" | "ok"> = {
  queued: "neutral",
  composing: "aqua",
  ready: "accent",
  printing: "aqua",
  completed: "ok",
  failed: "danger",
  expired: "warn",
};

/**
 * The print queue.
 *
 * This is an at-the-event screen: an operator opens it when a guest says their
 * print never came out, so it auto-refreshes and puts failures first.
 */
export function PrintQueuePage() {
  const toast = useToast();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [retrying, setRetrying] = useState<number | null>(null);

  const queue = useAsync(() => adminApi.printQueue({ page, status }), [page, status]);

  // Print state changes on hardware, not in this tab.
  useInterval(() => queue.reload(), 10000);

  async function retry(id: number) {
    setRetrying(id);
    try {
      await adminApi.retryPrintJob(id);
      toast.success("Print job requeued");
      queue.reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not retry");
    } finally {
      setRetrying(null);
    }
  }

  const jobs = queue.data?.print_jobs ?? [];
  const summary = queue.data?.summary ?? {};

  return (
    <CardSection
      eyebrow="Operations"
      title="Print queue"
      description="Live print jobs across your events. Refreshes automatically."
      actions={
        <div className="flex items-center gap-2">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="w-auto">
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONES).map((key) => <option key={key} value={key}>{key}</option>)}
          </Select>
          <Button size="sm" variant="outline" icon={<IconRefresh size={13} />} onClick={queue.reload}>Refresh</Button>
        </div>
      }
    >
      {Object.keys(summary).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary).map(([key, count]) => (
            <Badge key={key} tone={STATUS_TONES[key as PrintJobStatus] ?? "neutral"}>
              {count} {key}
            </Badge>
          ))}
        </div>
      )}

      {queue.loading && jobs.length === 0 ? (
        <div className="skeleton h-48" />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={<IconPrinter size={28} />}
          title="No print jobs"
          description="Jobs appear here as guests print from a booth."
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-600 text-left">
                {["Job", "Event", "Status", "Adapter", "Created", ""].map((heading) => (
                  <th key={heading} className="label-eyebrow whitespace-nowrap px-5 py-3">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-b border-ink-600/60 last:border-0">
                  <td className="px-5 py-3 font-mono text-xs text-haze-300">#{job.id}</td>
                  <td className="px-5 py-3 text-haze-100">{job.event_name ?? "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1">
                      <Badge tone={STATUS_TONES[job.status]}>{job.status}</Badge>
                      {job.error && <span className="max-w-xs text-xs text-danger">{job.error}</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-haze-400">
                    {job.adapter === "cups" ? `CUPS · ${job.printer_name ?? "no queue"}` : "Kiosk browser"}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-haze-400">{formatRelative(job.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {job.print_asset_url && (
                        <a
                          href={job.print_asset_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-accent hover:underline"
                        >
                          Asset
                        </a>
                      )}
                      {(job.status === "failed" || job.status === "expired") && (
                        <Button size="sm" variant="ghost" loading={retrying === job.id} onClick={() => void retry(job.id)}>
                          Retry
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {queue.data && queue.data.pagination.total_pages > 1 && (
        <nav className="flex items-center justify-center gap-3">
          <Button size="sm" disabled={page <= 1} onClick={() => setPage((v) => v - 1)}>Previous</Button>
          <span className="text-sm text-haze-400">
            Page {queue.data.pagination.current_page} of {queue.data.pagination.total_pages}
          </span>
          <Button size="sm" disabled={page >= queue.data.pagination.total_pages} onClick={() => setPage((v) => v + 1)}>Next</Button>
        </nav>
      )}
    </CardSection>
  );
}
